import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import { Routes, Route } from "react-router-dom";
import { useAuth } from "./auth/authContext";
import { setLockScreen } from "./store/slices/authSlice";
import versionManager from "./utils/versionManager";
import {
  handleMenuItemClickAsync,
  setActiveModalAsync,
} from "./store/thunks/appStateThunk";
import { setClickedItem as setClickedItemAction } from "./store/slices/appStateSlice";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { initializeClickTracking, cleanupClickTracking } from "./utils/globalClickListener";

import DashboardPage from "./pages/dashboardPage/DashboardPage";
import CliniciansPage from "./pages/clinicianPage/CliniciansPage";
import ClientsPage from "./pages/clientsPage/ClientsPage";
import SchedulePage from "./pages/schedulePage/SchedulePage";
import FormsPage from "./pages/formsPage/FormsPage";
import ReportsPage from "./pages/reportsPage/ReportsPage";
import HRPage from "./pages/hrPage/HRPage";
import ManagerPage from "./pages/managerPage/ManagerPage";
import LockScreen from "./auth/lockScreen";
import QuickBooks from "./pages/quickBooks/QuickBooksPage";
import UserBanner from "./applicationUi/components/UserBanner";
import QuickAddModal from "./modals/QuickAddModals";
import ClinicPage from "./pages/clinic/ClinicPage";
import ClinicianPayrollOverviewPage from "./pages/payrollPage/ClinicianPayrollOverviewPage";
import HRPayrollOverviewPage from "./pages/payrollPage/HRPayrollOverviewPage";
import HourlyPayrollPage from "./pages/payrollPage/HourlyPayrollPage";
import HRHourlyPayrollPage from "./pages/payrollPage/HRHourlyPayrollPage";
import ServicesPage from "./pages/services/ServicesPage";
import InvoicesPage from "./pages/invoicesPage/InvoicesPage";
import PaymentsPage from "./pages/paymentsPage/PaymentsPage";
import IntakesPage from "./pages/intakesPage/IntakesPage";
import ResourcePage from "./pages/resourcesPage/ResourcePage";
import ResourceSchedulePage from "./pages/ResourceSchedulePage";
import AdminLogsViewer from "./pages/dashboardPage/admin/AdminLogsViewer";
import ClickAnalyticsDashboard from "./pages/analytics/ClickAnalyticsDashboard";
// import FormSubmissionPage from "./pages/formsPage/FormSubmissionPage";
// import FormSubmittedPage from "./pages/formsPage/FormSubmittedPage";
// import PublicFormSubmissionPage from "./pages/formsPage/PublicFormSubmissionPage";

import {
  BanknotesIcon,
  CalendarIcon,
  CalendarDaysIcon,
  ChartPieIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  HomeIcon,
  UsersIcon,
  XMarkIcon,
  LockClosedIcon,
  UserGroupIcon,
  IdentificationIcon,
  BuildingOffice2Icon,
  ArrowLeftStartOnRectangleIcon,
  DocumentTextIcon,
  PuzzlePieceIcon,
  DocumentMagnifyingGlassIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import InsurerPage from "./pages/insurer/InsurerPage";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function App() {
  const { handleUserLogout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("Dashboard");
  const [isScreenLocked, setIsScreenLocked] = useState(false); // State to manage screen lock
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true); // Sidebar collapse state - collapsed by default
  const lastClickedMenuItem = useSelector(
    (state) => state.appState.selectedMenuItem
  );
  const clickedItem = useSelector((state) => state.appState.clickedItem); // Get clicked item from Redux

  // Initialize version management and click analytics on app start
  useEffect(() => {
    console.log('🔥 APP USEEFFECT RUNNING!');

    try {
      console.log('[App] 🎯 APP COMPONENT MOUNTED - Initializing version management system');
      console.log('[App] Version manager type:', typeof versionManager);
      console.log('[App] Version manager instance:', versionManager);
      console.log('[App] REACT_APP_VERSION:', process.env.REACT_APP_VERSION);
      console.log('[App] Cache bust version:', window.CACHE_BUST_VERSION);

      // Initialize click analytics tracking
      console.log('[App] 📊 Initializing click analytics tracking...');
      initializeClickTracking();

      // Delay to ensure everything is loaded
      setTimeout(() => {
        console.log('[App] Delayed version manager check...');

        // Ensure version manager is initialized
        if (versionManager && typeof versionManager.forceVersionCheck === 'function') {
          console.log('[App] ✅ Version manager is ready - triggering check');
          // Trigger initial version check
          versionManager.forceVersionCheck();
        } else {
          console.error('[App] ❌ Version manager not properly initialized');
          console.error('[App] versionManager:', versionManager);
          console.error('[App] Available methods:', versionManager ? Object.keys(versionManager) : 'N/A');
        }
      }, 1000);
    } catch (error) {
      console.error('🔥 APP USEEFFECT ERROR:', error);
    }

    return () => {
      // Cleanup version checking on unmount
      try {
        if (versionManager && typeof versionManager.stopVersionChecking === 'function') {
          versionManager.stopVersionChecking();
        }

        // Cleanup click analytics tracking
        console.log('[App] 📊 Cleaning up click analytics tracking...');
        cleanupClickTracking();
      } catch (error) {
        console.error('🔥 APP CLEANUP ERROR:', error);
      }
    };
  }, []);
  const activeModal = useSelector((state) => state.appState.activeModal);
  const roles = useSelector((state) => state.auth.user.role || []);
  const user = useSelector((state) => state.auth.user);

  const dispatch = useDispatch();
  const [navigation, setNavigation] = useState([]);

  // Check if the page was refreshed and set the menu to the lastClickedMenuItem
  useEffect(() => {
    if (lastClickedMenuItem && lastClickedMenuItem !== selectedMenuItem) {
      setSelectedMenuItem(lastClickedMenuItem);
      const updatedNavigation = navigation.map((item) => ({
        ...item,
        current: item.name === lastClickedMenuItem,
      }));
      setNavigation(updatedNavigation);
    }

    // Handle page refresh if there's an active modal
    if (activeModal) {
      // Re-open the active modal
      switch (activeModal) {
        case "quickAddClient":
          handleQuickAddClientClick();
          break;
        case "quickAddAppointment":
          handleQuickAddApptClick();
          break;
        case "scheduleModal":
          handleScheduleModalClick();
          break;
        // Add more cases for other modals as needed
        default:
          break;
      }
    }
  }, [lastClickedMenuItem, activeModal]);

  const menuAccessRules = (() => {
    // Default menu options for each role
    const defaultMenuAccess = {
      owner: [
        "Dashboard",
        "Clinicians",
        "Clients",
        "Week Schedule",
        "Forms",
        "Reports",
        "HR",
        "Manager",
        "QuickBooks",
        "Clinic",
        "Payroll",
        "HR Payroll",
        "Hourly Payroll",
        "HR Hourly Payroll",
        "Services",
        "Invoices",
        "Payments",
        "Intake",
        "Insurers",
        "Resources",
        "Admin Logs",
        "Analytics",
      ],
      admin: [
        "Dashboard",
        "Clinicians",
        "Clients",
        "Week Schedule",
        "Reports",
        "Services",
        "Invoices",
        "Payments",
        "Intake",
        "Insurers",
        "Resources",
        "Analytics",
      ],
      hr: [
        "Dashboard",
        "Clinicians",
        "Clients",
        "Week Schedule",
        "HR",
        "HR Payroll",
        "HR Hourly Payroll",
        "Reports",
        "Services",
        "Invoices",
        "Payments",
        "Intake",
        "Insurers",
        "Admin Logs",
        "Resources",
      ],
      clinician: ["Dashboard", "Clients", "Week Schedule"],
      bookkeeper: ["Dashboard", "QuickBooks"],
      officeManager: [
        "Dashboard",
        "Week Schedule",
        "Clinicians",
        "Clients",
        "Reports",
        "Clinic",
        "HR",
        "Intake",
        "Insurers",
        "Resources",
      ],
    };

    // Dynamically adjust menu based on `payrollType`
    const adjustedMenuAccess = { ...defaultMenuAccess };

    // Add "Payroll" or "Hourly Payroll" based on `payrollType`
    ["admin", "hr", "clinician", "bookkeeper", "officeManager"].forEach(
      (role) => {
        if (user?.payrollType === "fee for service") {
          adjustedMenuAccess[role] = [...adjustedMenuAccess[role], "Payroll"];
        } else if (user?.payrollType === "hourly") {
          adjustedMenuAccess[role] = [
            ...adjustedMenuAccess[role],
            "Hourly Payroll",
          ];
        }
      }
    );

    // Add Resource Schedule to the menuAccessRules
    // adjustedMenuAccess[role] = [...adjustedMenuAccess[role], "Resource Schedule"];

    return adjustedMenuAccess;
  })();

  useEffect(() => {
    // Combine all accessible menu items based on all roles
    const combinedMenuItems = new Set();
    roles.forEach((role) => {
      (menuAccessRules[role] || []).forEach((item) =>
        combinedMenuItems.add(item)
      );
    });

    // Set the navigation state based on combined menu items
    setNavigation(
      [
        { name: "Dashboard", icon: HomeIcon, current: true },
        { name: "Clinicians", icon: UsersIcon, current: false },
        { name: "Clients", icon: UserGroupIcon, current: false },
        { name: "Week Schedule", icon: CalendarDaysIcon, current: false },
        { name: "Forms", icon: DocumentDuplicateIcon, current: false },
        { name: "Reports", icon: ChartPieIcon, current: false },
        { name: "HR", icon: FolderIcon, current: false },
        { name: "Manager", icon: IdentificationIcon, current: false },
        { name: "QuickBooks", icon: BanknotesIcon, current: false },
        { name: "Clinic", icon: BuildingOffice2Icon, current: false },
        { name: "Payroll", icon: BanknotesIcon, current: false },
        { name: "HR Payroll", icon: BanknotesIcon, current: false },
        { name: "Hourly Payroll", icon: BanknotesIcon, current: false },
        { name: "HR Hourly Payroll", icon: BanknotesIcon, current: false },
        { name: "Services", icon: DocumentDuplicateIcon, current: false },
        { name: "Invoices", icon: DocumentTextIcon, current: false },
        { name: "Payments", icon: BanknotesIcon, current: false },
        { name: "Intake", icon: DocumentTextIcon, current: false },
        { name: "Insurers", icon: BuildingOffice2Icon, current: false },
        { name: "Resources", icon: PuzzlePieceIcon, current: false },
        { name: "Resource Schedule", icon: CalendarIcon, current: false },
        { name: "Admin Logs", icon: DocumentMagnifyingGlassIcon, current: false },
        { name: "Analytics", icon: ChartBarIcon, current: false },
      ].filter((item) => combinedMenuItems.has(item.name))
    );
  }, [roles]);

  // Function to toggle screen lock
  const toggleScreenLock = () => {
    dispatch(setLockScreen());
    setIsScreenLocked(!isScreenLocked);
  };

  const handleMenuItemClick = (name) => {
    setSelectedMenuItem(name);
    // Update the 'current' property of each menu item and set the state
    const updatedNavigation = navigation.map((item) => ({
      ...item,
      current: item.name === name,
    }));
    setNavigation(updatedNavigation); // Assuming you have a state for navigation
    dispatch(handleMenuItemClickAsync(name));
    dispatch(setClickedItemAction(null)); // Reset clickedItem when menu item changes
  };

  const handleQuickAddClientClick = () => {
    dispatch(setActiveModalAsync("client")); // Set the active modal to 'client'
  };

  const handleQuickAddApptClick = () => {
    dispatch(setActiveModalAsync("appointment")); // Set the active modal to 'appointment'
  };

  const handleScheduleModalClick = () => {
    dispatch(setActiveModalAsync("schedule"));
  };

  const handleResourceScheduleModalClick = () => {
    console.log("Resource Schedule Modal Clicked");
    dispatch(setActiveModalAsync("resource"));
  };

  const handleCloseQuickAddModal = () => {
    dispatch(setActiveModalAsync(null)); // Close the active modal
  };

  const handleSearchResultClick = (clickedItem) => {
    console.log("Search result clicked in App.js:", clickedItem);

    if (typeof clickedItem !== "object" || clickedItem === null) {
      console.log("Invalid clickedItem:", clickedItem);
      return;
    }

    const { type, name } = clickedItem;

    const updateNavigation = (targetName) => {
      const updatedNavigation = navigation.map((item) => ({
        ...item,
        current: item.name === targetName,
      }));
      setNavigation(updatedNavigation);
      setSelectedMenuItem(targetName);
      dispatch(setClickedItemAction(clickedItem));
    };

    switch (type) {
      case "Client":
        console.log("App.js: Setting clickedItem for client", { client: clickedItem });
        updateNavigation("Clients");
        dispatch(setClickedItemAction(clickedItem));
        break;

      case "User":
        updateNavigation("HR");
        break;

      case "QuickAddClient":
        handleQuickAddClientClick();
        break;

      case "QuickAddAppointment":
        handleQuickAddApptClick();
        break;

      case "ScheduleModal":
        handleScheduleModalClick();
        break;

      case "Service":
        updateNavigation("Services");
        break;

      case "Invoice":
        if (clickedItem.client && clickedItem.client._id) {
          // For invoices, navigate to the client page with invoices tab
          const clientItem = {
            ...clickedItem.client,
            type: "Client",
            selectedTab: "invoices",
            selectedInvoice: clickedItem
          };
          console.log("App.js: Setting clickedItem for invoice", {
            originalInvoice: clickedItem,
            newClientItem: clientItem
          });
          updateNavigation("Clients");
          dispatch(setClickedItemAction(clientItem));
        } else {
          // Fallback to invoices page if no client info
          updateNavigation("Invoices");
        }
        break;

      case "Intake":
        handleMenuItemClick(type);
        dispatch(setClickedItemAction(clickedItem));
        break;

      case "Insurers":
        updateNavigation("Insurers");
        break;

      case "Resource":
        updateNavigation("Resources");
        break;

      // For menu-related actions
      case "Dashboard":
      case "Clinicians":
      case "Week Schedule":
      case "Forms":
      case "Reports":
      case "HR":
      case "Manager":
      case "QuickBooks":
      case "Clinic":
        console.log("Clinic clicked", clickedItem);
        updateNavigation("Clinic");
        break;
      case "Payroll":
      case "HR Payroll":
      case "Payments":
      case "Resources":
      default:
        console.log("Unhandled item clicked:", clickedItem);
    }
  };

  return (
    <div>
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50 lg:hidden"
          onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full">
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0">
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setSidebarOpen(false)}>
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                {/* Mobile sidebar component */}
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white pb-2">
                  <div className="flex h-16 shrink-0 items-center px-6">
                    <img
                      className="h-12 w-auto"
                      src={`${process.env.PUBLIC_URL}/fancyTherapy.png`}
                      alt="HearSay Speech and Hearing Centre"
                    />
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <div className="flex flex-col h-full">
                      <ul className="flex-1 space-y-1 px-4">
                        {navigation.map((item) => (
                          <li key={item.name}>
                            <button
                              onClick={() => {
                                handleMenuItemClick(item.name);
                                setSidebarOpen(false);
                              }}
                              className={classNames(
                                item.current
                                  ? "bg-gray-50 text-indigo-600"
                                  : "text-gray-700 hover:text-indigo-600 hover:bg-gray-50",
                                "group flex gap-x-3 rounded-sm p-2 text-sm leading-6 font-semibold w-full items-center"
                              )}>
                              <item.icon
                                className={classNames(
                                  item.current
                                    ? "text-indigo-600"
                                    : "text-gray-400 group-hover:text-indigo-600",
                                  "h-6 w-6 shrink-0"
                                )}
                                aria-hidden="true"
                              />
                              {item.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-auto border-t border-gray-200 px-4 py-4 space-y-2">
                        <button
                          onClick={() => {
                            handleUserLogout();
                            setSidebarOpen(false);
                          }}
                          className="group flex items-center gap-x-3 rounded-sm p-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-indigo-600 w-full">
                          <ArrowLeftStartOnRectangleIcon className="h-6 w-6 text-gray-400 group-hover:text-indigo-600" />
                          Logout
                        </button>
                        {!isScreenLocked && (
                          <button
                            onClick={() => {
                              toggleScreenLock();
                              setSidebarOpen(false);
                            }}
                            className="group flex items-center gap-x-3 rounded-sm p-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-indigo-600 w-full">
                            <LockClosedIcon className="h-6 w-6 text-gray-400 group-hover:text-indigo-600" />
                            Lock Screen
                          </button>
                        )}
                      </div>
                    </div>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div
        className={classNames(
          "hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ease-in-out group",
          isSidebarCollapsed ? "lg:w-16 hover:lg:w-48" : "lg:w-48"
        )}>
        {/* Desktop sidebar component */}
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white">
          <div className="flex h-16 shrink-0 items-center px-6 justify-between">
            <img
              className={classNames(
                "h-12 w-auto transition-opacity duration-300",
                isSidebarCollapsed
                  ? "group-hover:opacity-100 opacity-0 absolute"
                  : "opacity-100"
              )}
              src={`${process.env.PUBLIC_URL}/fancyTherapy.png`}
              alt="HearSay Speech and Hearing Centre"
            />
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className={classNames(
                "p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-300",
                isSidebarCollapsed 
                  ? "opacity-0 group-hover:opacity-100" 
                  : "opacity-100"
              )}
              title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isSidebarCollapsed ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18" />
                </svg>
              )}
            </button>
          </div>
          <nav className="flex flex-1 flex-col min-h-0">
            <div className="flex flex-col flex-1">
              <ul className="flex-1 space-y-1 px-4 overflow-y-auto">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <button
                      onClick={() => handleMenuItemClick(item.name)}
                      className={classNames(
                        item.current
                          ? "bg-gray-50 text-indigo-600"
                          : "text-gray-700 hover:text-indigo-600 hover:bg-gray-50",
                        "group flex gap-x-3 rounded-sm p-2 text-sm leading-6 font-semibold w-full items-center whitespace-nowrap",
                        isSidebarCollapsed
                          ? "justify-center group-hover:justify-start"
                          : ""
                      )}>
                      <item.icon
                        className={classNames(
                          item.current
                            ? "text-indigo-600"
                            : "text-gray-400 group-hover:text-indigo-600",
                          "h-6 w-6 shrink-0"
                        )}
                        aria-hidden="true"
                      />
                      <span
                        className={classNames(
                          "transition-opacity duration-300",
                          isSidebarCollapsed
                            ? "opacity-0 hidden group-hover:opacity-100 group-hover:inline"
                            : "opacity-100 inline"
                        )}>
                        {item.name}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="flex-shrink-0 border-t border-gray-200 px-4 py-4 space-y-2">
                <button
                  onClick={handleUserLogout}
                  className={classNames(
                    "group flex items-center gap-x-3 rounded-sm p-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-indigo-600 w-full whitespace-nowrap",
                    isSidebarCollapsed
                      ? "justify-center group-hover:justify-start"
                      : ""
                  )}>
                  <ArrowLeftStartOnRectangleIcon className="h-6 w-6 text-gray-400 group-hover:text-indigo-600" />
                  <span
                    className={classNames(
                      "transition-opacity duration-300",
                      isSidebarCollapsed
                        ? "opacity-0 hidden group-hover:opacity-100 group-hover:inline"
                        : "opacity-100 inline"
                    )}>
                    Logout
                  </span>
                </button>
                {!isScreenLocked && (
                  <button
                    onClick={toggleScreenLock}
                    className={classNames(
                      "group flex items-center gap-x-3 rounded-sm p-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-indigo-600 w-full whitespace-nowrap",
                      isSidebarCollapsed
                        ? "justify-center group-hover:justify-start"
                        : ""
                    )}>
                    <LockClosedIcon className="h-6 w-6 text-gray-400 group-hover:text-indigo-600" />
                    <span
                      className={classNames(
                        "transition-opacity duration-300",
                        isSidebarCollapsed
                          ? "opacity-0 hidden group-hover:opacity-100 group-hover:inline"
                          : "opacity-100 inline"
                      )}>
                      Lock Screen
                    </span>
                  </button>
                )}
              </div>
            </div>
          </nav>
        </div>
      </div>

      {isScreenLocked && <LockScreen user={user} />}
      <main
        className={classNames(
          isScreenLocked ? "hidden" : "",
          isSidebarCollapsed ? "lg:pl-16" : "lg:pl-48"
        )}>
        {/* Add mobile menu button */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 lg:hidden">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}>
            <span className="sr-only">Open sidebar</span>
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
              />
            </svg>
          </button>
        </div>
        {/* UserBanner positioned to the right */}
        <div className="ml-auto">
          <UserBanner
            onQuickAddClientClick={handleQuickAddClientClick}
            onQuickAddApptClick={handleQuickAddApptClick}
            onScheduleModalClick={handleScheduleModalClick}
            onResourceScheduleModalClick={handleResourceScheduleModalClick}
            user={user}
            onSearchResultClick={handleSearchResultClick}
          />
        </div>

        <div className="px-4 sm:px-6 lg:px-8">
          <Routes>
            {/* Public form routes */}
            {/* <Route
              path="/forms/:formId"
              element={<PublicFormSubmissionPage />}
            />
            <Route path="/form-submitted" element={<FormSubmittedPage />} /> */}

            {/* Protected dashboard routes */}
            <Route
              path="/"
              element={
                <>
                  {selectedMenuItem === "Dashboard" && (
                    <DashboardPage user={user} clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Clinicians" && (
                    <CliniciansPage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Clients" && (
                    <ClientsPage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Week Schedule" && (
                    <SchedulePage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Forms" && (
                    <FormsPage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Reports" && (
                    <ReportsPage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "HR" && (
                    <HRPage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Manager" && (
                    <ManagerPage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "QuickBooks" && (
                    <QuickBooks clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Clinic" && (
                    <ClinicPage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Payroll" && (
                    <ClinicianPayrollOverviewPage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "HR Payroll" && (
                    <HRPayrollOverviewPage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Hourly Payroll" && (
                    <HourlyPayrollPage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "HR Hourly Payroll" && (
                    <HRHourlyPayrollPage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Services" && (
                    <ServicesPage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Invoices" && (
                    <InvoicesPage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Payments" && (
                    <PaymentsPage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Intake" && (
                    <IntakesPage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Insurers" && (
                    <InsurerPage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Resources" && (
                    <ResourcePage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Resource Schedule" && (
                    <ResourceSchedulePage clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Admin Logs" && (
                    <AdminLogsViewer clickedItem={clickedItem} />
                  )}
                  {selectedMenuItem === "Analytics" && (
                    <ClickAnalyticsDashboard clickedItem={clickedItem} />
                  )}
                </>
              }
            />
          </Routes>
        </div>
      </main>

      <QuickAddModal
        activeModal={activeModal}
        onClose={handleCloseQuickAddModal}
      />

      {/* Toast Container for global toast notifications */}
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{ zIndex: 9999 }}
      />
    </div>
  );
}
